import clsx from 'clsx';
import Image from 'next/image';
import * as React from 'react';
import { PersonIcon } from './icons';

const variantClasses = {
  circular: '[:where(&)]:rounded-full',
  rounded: '[:where(&)]:rounded-(--mui-shape-borderRadius)',
  square: '[:where(&)]:rounded-none'
} as const;

export type AvatarVariant = keyof typeof variantClasses;

export interface AvatarProps extends React.ComponentPropsWithoutRef<'div'> {
  alt?: string;
  src?: string;
  variant?: AvatarVariant;
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(function Avatar(
  { alt, children, className, src, variant = 'circular', ...rest },
  ref
) {
  const [errored, setErrored] = React.useState(false);

  React.useEffect(() => {
    setErrored(false);
  }, [src]);

  const showImage = Boolean(src) && !errored;

  return (
    <div
      ref={ref}
      className={clsx(
        // Defaults in :where() so a className (e.g. size-8, bg-*) wins — MUI's precedence.
        'relative flex [:where(&)]:size-10 shrink-0 items-center justify-center overflow-hidden select-none',
        '[:where(&)]:font-(family-name:--font-manrope) [:where(&)]:text-xl [:where(&)]:leading-none',
        variantClasses[variant],
        !showImage &&
          '[:where(&)]:bg-(--mui-palette-grey-400) [:where(&)]:text-(--mui-palette-background-default) [&_svg]:size-3/4',
        className
      )}
      {...rest}
    >
      {showImage && src ? (
        <Image
          alt={alt ?? ''}
          src={src}
          fill
          sizes="40px"
          onError={() => setErrored(true)}
          className="size-full object-cover text-transparent indent-[10000px]"
        />
      ) : (
        (children ?? <PersonIcon />)
      )}
    </div>
  );
});
